import { useState, useEffect } from 'react';

import type { ThemeColorModes } from '@atlaskit/tokens';
import { themeStringToObject } from '@atlaskit/tokens';

import { fg } from '@confluence/feature-gating';
import { getAGGClient } from '@confluence/graphql/entry-points/getAGGClient.ts';

import { ErrorSubjects, ErrorSubjectIds, reportThemingError } from './errorHandling.ts';
import { GlobalThemePreferenceQuery } from './graphql/GlobalThemePreferenceQuery.graphql.ts';
import type {
	GlobalThemePreferenceQuery as GlobalThemePreferenceQueryType,
	GlobalThemePreferenceQueryVariables,
} from './graphql/__types__/GlobalThemePreferenceQuery.ts';

const DEFAULT_COLOR_MODE: ThemeColorModes = 'auto';

/**
 * Hook to fetch the global (account-level) color mode from AGG on-demand.
 * This hook queries the shared user preferences API when the feature flag is enabled.
 * It only executes on the client side and returns undefined during SSR or when disabled.
 *
 * @param accountId - The user's account ID
 * @returns The user's global color mode preference, or undefined if not available/disabled
 */
export const useGlobalColorMode = (accountId: string | null): ThemeColorModes | undefined => {
	const [colorMode, setColorMode] = useState<ThemeColorModes | undefined>(undefined);

	useEffect(() => {
		// Skip if feature flag is disabled or no account ID
		if (!fg('shared-user-preferences') || !accountId) {
			return;
		}

		// Query AGG for the user's global (account-level) theme preference
		const client = getAGGClient();

		// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
		client
			.query<GlobalThemePreferenceQueryType, GlobalThemePreferenceQueryVariables>({
				query: GlobalThemePreferenceQuery,
				variables: { accountId },
				fetchPolicy: 'cache-first',
			})
			.then(({ data }) => {
				const themeString = data?.settings_userPreferencesGlobal?.theme;
				if (themeString) {
					const themeObject = themeStringToObject(themeString);
					setColorMode(themeObject.colorMode || DEFAULT_COLOR_MODE);
				} else {
					// Data loaded but no theme string
					setColorMode(DEFAULT_COLOR_MODE);
				}
			})
			.catch((error) => {
				// Report query errors to analytics
				void reportThemingError(
					ErrorSubjects.useGlobalColorMode,
					ErrorSubjectIds.apolloQueryErrors,
					{
						accountId,
						error: error.message,
						errorName: error.name,
					},
				);
				setColorMode(DEFAULT_COLOR_MODE);
			});
	}, [accountId]);

	// Return undefined if feature flag is disabled
	return fg('shared-user-preferences') ? colorMode : undefined;
};
